export const androidReleaseManifestUrl = 'https://downloads.brandfieldcrew.com/android/latest.json';

const downloadsOrigin = 'https://downloads.brandfieldcrew.com';
const productionApiBaseUrl = 'https://api.brandfieldcrew.com/api/v1';
const productionPackageId = 'com.brandfieldcrew.ba';

export type AndroidArchitecture = 'arm64' | 'armv7';

interface AndroidDownload {
  label: string;
  detail: string;
  size: string;
  sha256: string;
  url: string;
}

export interface AndroidRelease {
  version: string;
  versionCode: number;
  minimumAndroid: string;
  releasedOn: string;
  architectures: Record<AndroidArchitecture, AndroidDownload>;
}

export const fallbackAndroidRelease: AndroidRelease = {
  version: '1.5.0',
  versionCode: 9,
  minimumAndroid: 'Android 7.0',
  releasedOn: '28 August 2026',
  architectures: {
    arm64: {
      label: 'Modern Android phones',
      detail: 'Recommended for most phones made since 2017',
      size: '40.42 MB',
      sha256: 'fc0ecce41b6b3baaafccdf4bcceeee438eb328cb5e1557b76041d1ee7261bb7b',
      url: 'https://downloads.brandfieldcrew.com/android/1.5.0/BrandFieldCrew-1.5.0-arm64-v8a.apk',
    },
    armv7: {
      label: 'Older 32-bit Android phones',
      detail: 'Use only when the recommended build will not install',
      size: '32.97 MB',
      sha256: '99e41ecaf98a99b04324d67e30fe7bb7eed31f3437e4539ed655f6c620c8d56b',
      url: 'https://downloads.brandfieldcrew.com/android/1.5.0/BrandFieldCrew-1.5.0-armeabi-v7a.apk',
    },
  },
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readString(record: Record<string, unknown>, key: string): string {
  const value = record[key];
  if (typeof value !== 'string' || value.length === 0) {
    throw new Error(`Invalid Android release manifest field: ${key}`);
  }
  return value;
}

function formatSize(bytes: number): string {
  return `${(bytes / 1_000_000).toFixed(2)} MB`;
}

function formatReleaseDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    throw new Error('Invalid Android release timestamp.');
  }
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'long',
    timeZone: 'Asia/Karachi',
    year: 'numeric',
  }).format(date);
}

function parseDownload(
  files: unknown[],
  architecture: 'arm64-v8a' | 'armeabi-v7a',
  version: string,
): AndroidDownload {
  const matches = files.filter(
    (file): file is Record<string, unknown> => isRecord(file) && file.architecture === architecture,
  );
  if (matches.length !== 1) {
    throw new Error(`Android release manifest requires one ${architecture} file.`);
  }

  const file = matches[0];
  if (!file) throw new Error(`Android release manifest requires one ${architecture} file.`);
  const filename = readString(file, 'filename');
  const shortArchitecture = architecture === 'arm64-v8a' ? 'arm64' : 'armeabi';
  const expectedFilenames = [
    `BFC-${version}-${shortArchitecture}.apk`,
    `BrandFieldCrew-${version}-${architecture}.apk`,
  ];
  const url = readString(file, 'download_url');
  const expectedUrl = `${downloadsOrigin}/android/${version}/${filename}`;
  const bytes = file.bytes;
  const sha256 = readString(file, 'sha256');

  if (!expectedFilenames.includes(filename) || url !== expectedUrl) {
    throw new Error(`Android release manifest contains an unexpected ${architecture} download.`);
  }
  if (!Number.isSafeInteger(bytes) || typeof bytes !== 'number' || bytes <= 0) {
    throw new Error(`Android release manifest contains an invalid ${architecture} size.`);
  }
  if (!/^[a-f0-9]{64}$/.test(sha256)) {
    throw new Error(`Android release manifest contains an invalid ${architecture} checksum.`);
  }

  const copy = architecture === 'arm64-v8a'
    ? {
        label: 'Modern Android phones',
        detail: 'Recommended for most phones made since 2017',
      }
    : {
        label: 'Older 32-bit Android phones',
        detail: 'Use only when the recommended build will not install',
      };

  return { ...copy, size: formatSize(bytes), sha256, url };
}

export function parseAndroidReleaseManifest(value: unknown): AndroidRelease {
  if (!isRecord(value)) throw new Error('Invalid Android release manifest.');
  if (value.schema_version !== 1 || value.environment !== 'production') {
    throw new Error('Unsupported Android release manifest.');
  }
  if (value.package_id !== productionPackageId || value.api_base_url !== productionApiBaseUrl) {
    throw new Error('Android release manifest does not describe the Production application.');
  }

  const version = readString(value, 'version');
  const versionCode = value.version_code;
  const generatedAt = readString(value, 'generated_at');
  const files = value.files;

  if (!/^\d+\.\d+\.\d+$/.test(version)) {
    throw new Error('Android release manifest contains an invalid version.');
  }
  if (!Number.isSafeInteger(versionCode) || typeof versionCode !== 'number' || versionCode <= 0) {
    throw new Error('Android release manifest contains an invalid version code.');
  }
  if (!Array.isArray(files)) {
    throw new Error('Android release manifest contains no files.');
  }

  return {
    version,
    versionCode,
    minimumAndroid: 'Android 7.0',
    releasedOn: formatReleaseDate(generatedAt),
    architectures: {
      arm64: parseDownload(files, 'arm64-v8a', version),
      armv7: parseDownload(files, 'armeabi-v7a', version),
    },
  };
}
